import React, { useState, useMemo } from 'react';

import PlannerIcons from '../../components/shared/PlannerIcons';

export default function ProjectGifts({ project, updateProject }) {
  const [newGift, setNewGift] = useState({ 
    item: '', 
    giver: '', 
    notes: '', 
    value: '', 
    receivedAt: new Date().toISOString().split('T')[0] 
  });
  const [searchQuery, setSearchQuery] = useState('');
  
  const gifts = project.gifts || [];

  // Robust calculations using useMemo to keep UI snappy
  const stats = useMemo(() => {
    const sentCount = gifts.filter(g => g.thankYouSent).length;
    const progress = gifts.length ? Math.round((sentCount / gifts.length) * 100) : 0;
    const totalValue = gifts.reduce((acc, g) => acc + (parseFloat(g.value) || 0), 0);
    
    return {
      progress,
      sentCount,
      totalCount: gifts.length,
      totalValue
    };
  }, [gifts]);

  // Handle live search filtering
  const filteredGifts = useMemo(() => {
    if (!searchQuery.trim()) return gifts;
    const q = searchQuery.toLowerCase();
    return gifts.filter(g => 
        g.item.toLowerCase().includes(q) || 
        g.giver.toLowerCase().includes(q) || 
        (g.notes || '').toLowerCase().includes(q)
    );
  }, [gifts, searchQuery]);

  const addGift = (e) => {
    e.preventDefault();
    if(!newGift.item || !newGift.giver) return;
    
    updateProject(project.id, { 
      gifts: [
        { 
            id: Date.now().toString(), 
            ...newGift, 
            thankYouSent: false,
            createdAt: new Date().toISOString() 
        },
        ...(project.gifts || [])
      ] 
    });
    
    setNewGift({ 
        item: '', 
        giver: '', 
        notes: '', 
        value: '', 
        receivedAt: new Date().toISOString().split('T')[0] 
    });
  };

  const toggleThankYou = (id, currentStatus) => {
      updateProject(project.id, { 
        gifts: project.gifts.map(g => g.id === id ? { ...g, thankYouSent: !currentStatus } : g) 
      });
  };

  const removeGift = (id) => {
    if (window.confirm("Remove this gift from the tracker?")) {
        updateProject(project.id, { gifts: project.gifts.filter(g => g.id !== id) });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
       {/* Header & Robust Stats Dashboard */}
       <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
           <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-6 text-white">
                <h3 className="text-xl font-bold flex items-center gap-2"><span>🎁</span> Gift & Thank You Tracker</h3>
                <p className="text-pink-100 text-sm mt-1 opacity-90">Keep track of every gesture and ensure no thank you note is left behind.</p>
           </div>
           
           <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
                <div className="p-4 text-center">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Gifts</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalCount}</p>
                </div>
                <div className="p-4 text-center">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Estimated Value</p>
                    <p className="text-2xl font-bold text-emerald-600">${stats.totalValue.toLocaleString()}</p>
                </div>
                <div className="p-4 text-center">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Notes Sent</p>
                    <p className="text-2xl font-bold text-pink-600">{stats.sentCount}</p>
                </div>
                <div className="p-4 flex flex-col items-center justify-center">
                    <div className="w-full max-w-[100px]">
                        <div className="flex justify-between text-[9px] font-bold text-gray-400 mb-1 uppercase">
                            <span>Completion</span>
                            <span>{stats.progress}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-pink-500 rounded-full transition-all duration-700" style={{ width: `${stats.progress}%` }} />
                        </div>
                    </div>
                </div>
           </div>
       </div>

       {/* Add Gift Form - Comprehensive fields for better tracking */}
       <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 bg-pink-500 h-full"></div>
          <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Log New Received Gift</h4>
          <form onSubmit={addGift} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div className="lg:col-span-1">
                <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Gift / Item</label>
                <input 
                    type="text" 
                    value={newGift.item} 
                    onChange={e => setNewGift({...newGift, item: e.target.value})} 
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" 
                    required 
                    placeholder="e.g. KitchenAid Mixer" 
                />
            </div>
            <div className="lg:col-span-1">
                <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Given By</label>
                <input 
                    type="text" 
                    value={newGift.giver} 
                    onChange={e => setNewGift({...newGift, giver: e.target.value})} 
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" 
                    required 
                    placeholder="e.g. The Millers" 
                />
            </div>
            <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Date Received</label>
                <input 
                    type="date" 
                    value={newGift.receivedAt} 
                    onChange={e => setNewGift({...newGift, receivedAt: e.target.value})} 
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" 
                />
            </div>
            <div>
                <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Est. Value ($)</label>
                <input 
                    type="number" 
                    value={newGift.value} 
                    onChange={e => setNewGift({...newGift, value: e.target.value})} 
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" 
                    placeholder="0.00"
                />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
                <label className="text-[10px] font-bold text-gray-500 uppercase mb-1 block">Internal Notes (Optional)</label>
                <input 
                    type="text" 
                    value={newGift.notes} 
                    onChange={e => setNewGift({...newGift, notes: e.target.value})} 
                    placeholder="e.g. Delivered to home, fragile..." 
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-pink-500 outline-none transition" 
                />
            </div>
            <button type="submit" className="w-full bg-gray-900 text-white p-3 rounded-xl text-sm font-black hover:bg-black transition shadow-lg transform active:scale-95">
                Add to List
            </button>
          </form>
       </div>

       {/* Live Search functionality */}
       <div className="flex bg-white p-2 rounded-2xl border border-gray-200 shadow-sm max-w-md">
            <div className="pl-3 pr-2 py-2 text-gray-400">
                <PlannerIcons.Search />
            </div>
            <input 
                type="text" 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by giver, item, or notes..." 
                className="w-full bg-transparent border-none focus:ring-0 text-sm font-medium text-gray-700"
            />
       </div>

        {/* Gift List Grid */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
              {/* Desktop Table View */}
              <table className="hidden md:table w-full text-sm text-left">
                  <thead className="bg-gray-50 text-[10px] uppercase font-black text-gray-400 tracking-widest border-b border-gray-100">
                      <tr>
                          <th className="px-6 py-4">Item Details</th>
                          <th className="px-6 py-4">Giver</th>
                          <th className="px-6 py-4 text-center">Value</th>
                          <th className="px-6 py-4 text-center">Thank You Sent?</th>
                          <th className="px-6 py-4 text-right">Action</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                      {filteredGifts.length > 0 ? filteredGifts.map((gift) => (
                          <tr key={gift.id} className={`hover:bg-gray-50 transition group ${gift.thankYouSent ? 'opacity-60' : ''}`}>
                              <td className="px-6 py-4">
                                  <div className="flex flex-col">
                                      <span className={`font-bold text-gray-900 ${gift.thankYouSent ? 'line-through text-gray-400' : ''}`}>{gift.item}</span>
                                      <span className="text-[10px] text-gray-400 font-bold uppercase mt-0.5">Rec'd {new Date(gift.receivedAt).toLocaleDateString()}</span>
                                      {gift.notes && <span className="text-[11px] text-gray-500 italic mt-1">"{gift.notes}"</span>}
                                  </div>
                              </td>
                              <td className="px-6 py-4 font-semibold text-gray-700">{gift.giver}</td>
                              <td className="px-6 py-4 text-center font-mono font-bold text-gray-500">
                                  {gift.value ? `$${parseFloat(gift.value).toFixed(2)}` : '--'}
                              </td>
                              <td className="px-6 py-4 text-center">
                                  <input 
                                      type="checkbox" 
                                      checked={gift.thankYouSent} 
                                      onChange={() => toggleThankYou(gift.id, gift.thankYouSent)}
                                      className="h-6 w-6 text-pink-600 rounded-lg border-2 border-gray-300 focus:ring-pink-500 cursor-pointer transition-transform active:scale-90"
                                  />
                              </td>
                              <td className="px-6 py-4 text-right">
                                  <button onClick={() => removeGift(gift.id)} className="text-gray-400 hover:text-red-600 transition opacity-0 group-hover:opacity-100 p-2 hover:bg-red-50 rounded-full">
                                      <PlannerIcons.Trash />
                                  </button>
                              </td>
                          </tr>
                      )) : (
                          <tr><td colSpan="5" className="p-20 text-center text-gray-400">
                              <div className="text-5xl mb-4 grayscale opacity-40">🎁</div>
                              <p className="font-bold text-gray-500">No gifts found.</p>
                              <p className="text-sm">Start logging items from your shower, registry, or wedding day.</p>
                          </td></tr>
                      )}
                  </tbody>
              </table>

              {/* Mobile Cards View */}
              <div className="md:hidden flex flex-col divide-y divide-gray-100">
                  {filteredGifts.length > 0 ? filteredGifts.map((gift) => (
                      <div key={gift.id} className={`p-5 flex flex-col gap-3 transition-colors ${gift.thankYouSent ? 'bg-gray-50/50 opacity-75' : 'bg-white'}`}>
                          <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <span className={`font-black text-lg leading-tight block ${gift.thankYouSent ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{gift.item}</span>
                                <span className="text-[10px] text-gray-400 font-black uppercase mt-1 block">From: {gift.giver}</span>
                              </div>
                              <button onClick={() => removeGift(gift.id)} className="p-2 text-gray-400 hover:text-red-500 bg-gray-50 rounded-xl"><PlannerIcons.Trash /></button>
                          </div>
                          
                          {gift.notes && <p className="text-xs text-gray-500 italic bg-gray-50 p-2 rounded-lg border border-gray-100">{gift.notes}</p>}
                          
                          <div className="flex items-center justify-between mt-2">
                             <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Value</span>
                                <span className="font-mono font-bold text-gray-600">{gift.value ? `$${parseFloat(gift.value).toFixed(2)}` : '--'}</span>
                             </div>
                             <label className={`flex items-center gap-3 px-4 py-2 rounded-xl border-2 transition-all ${gift.thankYouSent ? 'bg-pink-50 border-pink-200 text-pink-700' : 'bg-white border-gray-200 text-gray-600'}`}>
                                <span className="text-xs font-black uppercase tracking-widest">{gift.thankYouSent ? 'Sent' : 'Thank Giver'}</span>
                                <input 
                                    type="checkbox" 
                                    checked={gift.thankYouSent} 
                                    onChange={() => toggleThankYou(gift.id, gift.thankYouSent)}
                                    className="h-6 w-6 text-pink-600 rounded-lg border-gray-300 focus:ring-pink-500"
                                />
                             </label>
                          </div>
                      </div>
                  )) : (
                      <div className="p-16 text-center text-gray-400 italic bg-gray-50">
                          <p>No matching gifts found.</p>
                      </div>
                  )}
              </div>
          </div>
        </div>
    </div>
  );
}